import { getDefaultClient } from '@dynamic-labs-sdk/client';
import {
  MethodNotImplementedError,
  getWalletProviderFromWalletAccount,
} from '@dynamic-labs-sdk/client/core';

import type { BitcoinWalletAccount } from '../BitcoinWalletAccount.types';
import { NotBitcoinProviderError } from '../errors/NotBitcoinProviderError';
import { isBitcoinWalletProvider } from '../isBitcoinWalletProvider';
import type {
  FeePriority,
  WaasBitcoinTransaction,
} from '../waas/WaasBitcoinWalletProvider.types';

type BuildPsbtParams = {
  transaction: WaasBitcoinTransaction;
  walletAccount: BitcoinWalletAccount;
};

type BuildPsbtResult = {
  feePriority: FeePriority;
  unsignedPsbtBase64: string;
};

/**
 * Builds an unsigned PSBT for a Bitcoin transaction.
 * This is only available for WaaS (embedded) wallets.
 *
 * @param params.transaction - The transaction details including recipient and amount
 * @param params.walletAccount - The wallet account to build the PSBT for
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns An object containing the unsigned PSBT in base64 format and the fee priority used
 * @throws {MethodNotImplementedError} If the wallet provider does not implement the buildPsbt method
 * @throws {NotBitcoinProviderError} If the wallet account is not a valid BitcoinWalletAccount
 */
export const buildPsbt = async (
  { walletAccount, transaction }: BuildPsbtParams,
  client = getDefaultClient()
): Promise<BuildPsbtResult> => {
  const provider = getWalletProviderFromWalletAccount(
    {
      walletAccount,
    },
    client
  );

  if (!isBitcoinWalletProvider(provider)) {
    throw new NotBitcoinProviderError({ providerKey: provider.key });
  }

  // buildPsbt is only available on WaaS providers
  if (!('buildPsbt' in provider) || typeof provider.buildPsbt !== 'function') {
    throw new MethodNotImplementedError('buildPsbt');
  }

  const unsignedPsbtBase64 = await provider.buildPsbt({ transaction, walletAccount });

  return {
    feePriority: transaction.feePriority ?? 'medium',
    unsignedPsbtBase64,
  };
};

